import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { PUBLIC_ICON, PUBLIC_IMAGE } from "../../constants";
import styles from "./Credits.module.scss";
import { CreditMarket, compactUsd, estimateCredit, fetchCreditMarkets, percent } from "./creditMarkets";

const amountValue = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const assetAmount = (value: number) => new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
}).format(value);

const marketUrl = (market: CreditMarket) =>
  `https://app.morpho.org/base/market/${market.marketId}`;

const Credits = () => {
  const [markets, setMarkets] = useState<CreditMarket[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [collateralAmount, setCollateralAmount] = useState("");
  const [borrowAmount, setBorrowAmount] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetchCreditMarkets(controller.signal)
      .then((result) => {
        setMarkets(result.markets);
        setSelectedId((current) => current || result.markets.find((market) => market.executionEnabled)?.marketId || result.markets[0]?.marketId || "");
      })
      .catch((reason) => {
        if (reason?.name !== "AbortError") setError("Live credit markets are temporarily unavailable.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [reloadKey]);

  const selected = markets.find((market) => market.marketId === selectedId) || null;
  const visibleMarkets = useMemo(() => {
    const query = search.trim().toLowerCase();
    return markets.filter((market) => !query || `${market.collateralAsset.symbol} ${market.loanAsset.symbol}`.toLowerCase().includes(query)).slice(0, 14);
  }, [markets, search]);
  const collateralAssets = useMemo(() => {
    const totals = new Map<string, number>();
    markets.forEach((market) => totals.set(market.collateralAsset.symbol, (totals.get(market.collateralAsset.symbol) || 0) + market.liquidityUsd));
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [markets]);
  const estimate = selected ? estimateCredit(selected, amountValue(collateralAmount), amountValue(borrowAmount)) : null;
  const canOpenMarket = Boolean(selected?.executionEnabled && amountValue(collateralAmount) > 0 && amountValue(borrowAmount) > 0 && !estimate?.exceedsLltv && !estimate?.exceedsLiquidity);

  const chooseMarket = (market: CreditMarket) => {
    setSelectedId(market.marketId);
    setCollateralAmount("");
    setBorrowAmount("");
  };

  const chooseCollateral = (symbol: string) => {
    const market = markets.find((item) => item.collateralAsset.symbol === symbol && item.executionEnabled)
      || markets.find((item) => item.collateralAsset.symbol === symbol);
    if (market) chooseMarket(market);
    document.getElementById("credit-workspace")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return <>
    <Helmet>
      <title>Crypto-backed credit · Alfa Crypto</title>
      <link rel="icon" href={PUBLIC_ICON + "Alfa.svg"} />
    </Helmet>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>Crypto-backed credit on Base</span>
          <h1>Access liquidity.<br />Keep your crypto.</h1>
          <p>Compare live Morpho markets, understand liquidation risk and calculate a position before connecting your wallet.</p>
          <div className={styles.heroActions}>
            <button onClick={() => document.getElementById("credit-workspace")?.scrollIntoView({ behavior: "smooth" })}>Explore markets</button>
            <a href="#how-it-works">How borrowing works</a>
          </div>
        </div>
        <div className={styles.heroVisual}>
          <img src={PUBLIC_IMAGE + "getCrypto__img.svg"} alt="Crypto-backed credit" />
        </div>
      </section>

      <section className={styles.creditSection} id="credit-workspace">
        <div className={styles.sectionHeading}>
          <span className={styles.eyebrow}>Live credit desk</span>
          <h2>Choose a market and model your position</h2>
          <p>All rates, liquidity and collateral limits below come from currently listed Morpho markets on Base.</p>
        </div>

        {error ? <div className={styles.errorState}><strong>Markets could not be loaded</strong><p>{error}</p><button onClick={() => setReloadKey((key) => key + 1)}>Try again</button></div> : <div className={styles.workspace}>
          <section className={styles.marketPanel}>
            <div className={styles.assetStrip}>
              {collateralAssets.map(([symbol, liquidity]) => <button key={symbol} className={selected?.collateralAsset.symbol === symbol ? styles.activeAsset : ""} onClick={() => chooseCollateral(symbol)}><strong>{symbol}</strong><span>{compactUsd(liquidity)} liquidity</span></button>)}
            </div>
            <div className={styles.marketToolbar}>
              <div><strong>Credit markets</strong><span>Select collateral and the asset you want to borrow</span></div>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search BTC, ETH, USDC…" aria-label="Search credit markets" />
            </div>
            <div className={styles.marketHead}><span>Pair</span><span>Borrow APY</span><span>Liquidity</span><span>LLTV</span></div>
            <div className={styles.marketList}>
              {loading ? <div className={styles.loading}>Loading live markets…</div> : visibleMarkets.length ? visibleMarkets.map((market) => <button key={market.marketId} className={selectedId === market.marketId ? styles.activeMarket : ""} onClick={() => chooseMarket(market)}>
                <span className={styles.pair}><strong>{market.collateralAsset.symbol}</strong><i>→</i><strong>{market.loanAsset.symbol}</strong><small className={market.executionEnabled ? styles.available : styles.unavailable}>{market.executionEnabled ? "Available" : "Review only"}</small></span>
                <b>{percent(market.borrowApy)}</b><b>{compactUsd(market.liquidityUsd)}</b><b>{market.lltvPercent.toFixed(1)}%</b>
              </button>) : <div className={styles.loading}>No markets match your search.</div>}
            </div>
          </section>

          <aside className={styles.builder}>
            <div className={styles.builderHead}><span>Position calculator</span><strong>{selected ? `${selected.collateralAsset.symbol} / ${selected.loanAsset.symbol}` : "Select a market"}</strong></div>
            {selected && <>
              <label>Collateral amount <small>{selected.collateralAsset.symbol}</small><input inputMode="decimal" value={collateralAmount} onChange={(event) => setCollateralAmount(event.target.value.replace(/[^0-9.,]/g, ""))} placeholder="0.00" /></label>
              <div className={styles.availableCredit}><span>Estimated collateral value<strong>{compactUsd(estimate?.collateralValueUsd || 0)}</strong></span><span>Maximum credit<strong>{assetAmount(estimate?.maxBorrowLoanAssets || 0)} {selected.loanAsset.symbol}</strong></span></div>
              <label>Borrow amount <small>{selected.loanAsset.symbol}</small><input inputMode="decimal" value={borrowAmount} onChange={(event) => setBorrowAmount(event.target.value.replace(/[^0-9.,]/g, ""))} placeholder="0.00" className={estimate?.exceedsLltv || estimate?.exceedsLiquidity ? styles.invalid : ""} /></label>
              <div className={styles.riskMeter}><span style={{ width: `${Math.min(100, (estimate?.ltvPercent || 0) / selected.lltvPercent * 100)}%` }} /></div>
              <div className={styles.metrics}>
                <div><span>Your LTV</span><strong>{(estimate?.ltvPercent || 0).toFixed(2)}%</strong></div><div><span>Liquidation LLTV</span><strong>{selected.lltvPercent.toFixed(2)}%</strong></div><div><span>Borrow APY</span><strong>{percent(selected.borrowApy)}</strong></div><div><span>Utilization</span><strong>{percent(selected.utilization)}</strong></div>
              </div>
              {estimate?.exceedsLltv && <p className={styles.warning}>Reduce the credit amount: this position reaches the market’s liquidation threshold.</p>}
              {estimate?.exceedsLiquidity && <p className={styles.warning}>The requested amount exceeds the market’s available liquidity.</p>}
              {!selected.executionEnabled && <p className={styles.warning}>This market has missing price data or a critical provider warning. Review only.</p>}
              <a className={`${styles.marketCta} ${canOpenMarket ? "" : styles.marketCtaDisabled}`} href={canOpenMarket ? marketUrl(selected) : undefined} target="_blank" rel="noreferrer" aria-disabled={!canOpenMarket}>Continue on Morpho</a>
              <small className={styles.ctaNote}>The transaction is reviewed and signed in your wallet on Morpho. Alfa Crypto never receives your private key.</small>
            </>}
          </aside>
        </div>}
      </section>

      <section className={styles.how} id="how-it-works">
        <div className={styles.sectionHeading}><span className={styles.eyebrow}>Simple, transparent flow</span><h2>Three steps from crypto to liquidity</h2></div>
        <div className={styles.steps}>
          <article><span>01</span><h3>Choose a market</h3><p>Compare the collateral pair, variable borrow APY, available liquidity and liquidation LLTV.</p></article>
          <article><span>02</span><h3>Model the position</h3><p>Enter collateral and credit amounts. Check your estimated LTV before opening the provider market.</p></article>
          <article><span>03</span><h3>Sign in your wallet</h3><p>Review Morpho’s on-chain terms, connect a compatible wallet and approve the transaction yourself.</p></article>
        </div>
      </section>

      <section className={styles.riskSection}>
        <div><span className={styles.eyebrow}>Borrow responsibly</span><h2>Know what changes after you borrow</h2><p>Rates are variable and positions can be liquidated. The selected market’s on-chain oracle and contract terms are authoritative.</p></div>
        <div className={styles.riskGrid}>
          <article><strong>Variable APY</strong><p>Borrowing cost changes with market utilization.</p></article><article><strong>Liquidation</strong><p>If your LTV reaches the market limit, collateral may be sold.</p></article><article><strong>Market liquidity</strong><p>Available credit can change before your transaction is confirmed.</p></article><article><strong>Wallet control</strong><p>You approve every on-chain action directly in your wallet.</p></article>
        </div>
      </section>

      <section className={styles.faq}>
        <div className={styles.sectionHeading}><span className={styles.eyebrow}>Before you borrow</span><h2>Frequently asked questions</h2></div>
        <details><summary>Do I sell my collateral?</summary><p>No. You supply collateral to the selected Morpho market and borrow another asset against it, subject to that market’s contract terms.</p></details>
        <details><summary>What is LLTV?</summary><p>LLTV is the liquidation loan-to-value threshold. A position at or above this limit can be liquidated.</p></details>
        <details><summary>Does Alfa Crypto hold my funds?</summary><p>No. Market execution is non-custodial and takes place through Morpho on Base. You sign the transaction in your own wallet.</p></details>
        <details><summary>Are the displayed terms guaranteed?</summary><p>No. APY, liquidity and prices can change between calculation and transaction confirmation. Always review the final wallet transaction and provider terms.</p></details>
      </section>
    </main>
  </>;
};

export default Credits;
